import React from 'react';
import { ArType } from '@api/services/lookup/types';
import { ErrorMessages } from '@utils/enums';
import { formatCoordFlt, isLatLngOutsideUs } from '@utils/helpers';
import * as yup from 'yup';
import { Alert } from '@trussworks/react-uswds';
import _startCase from 'lodash/startCase';
import { arTableInputColumnData } from '@src/app-components/table-input/tableInput.helpers.ars';

type AquaticResourceRow = {
  aquaticResourceID?: number | string;
  aquaticResourceType?: string;
  cowardinCode?: string;
  hgmCode?: string;
  jurisdictionalDeterminationType?: string;
  latitude: string;
  localWaterway?: string;
  longitude: string;
  measurementAmount: string;
  measurementType: string;
  measurementUnit: string;
  regulationType: string;
  rowIssues?: string[];
  state: string;
  waterName: string;
};

type AquaticResourceValidationContext = {
  aquaticResourceMismatchIds?: number[];
  aquaticResourceGeometryBoundaryFailureIds?: number[];
  aquaticResourceDuplicateWaterNameIds?: number[];
  updatedRowData?: AquaticResourceRow;
  waterNameCounts?: Map<string, number>;
};

type AquaticResourceValidation = {
  hasStateCoordinateMismatch: boolean;
  hasDuplicateWaterName: boolean;
};

type GeoJsonFeature = {
  id?: string | number;
  properties?: {
    waterName?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

type LatLong = {
  latitude: number;
  longitude: number;
};

type Option = {
  value: string;
  label: string;
};

type StateArTypes = {
  [key: string]: string[]; // key is state abbreviation, value is array of AR Type shortCodes applicable in that state
};

type StateRegulationType = {
  stateRegulationID: number;
  stateABBR: string;
  state: string;
  regulationType: string;
  asOfDate: string;
};

type AquaticResourcesFormType = 'permit' | 'jd';

type AquaticResourcesConfigContext = {
  jdType?: string | null;
  determination?: string;
};

type UpdateSectionEnrichment = {
  valid: boolean;
  label?: string;
  dirty?: boolean;
};

type YupObjectSchema = ReturnType<typeof yup.object>;

type AquaticResourcesConfig = {
  formType: AquaticResourcesFormType;
  isPermit: boolean;
  isJd: boolean;
  metadata: {
    sectionName: string;
  };
  getDefaultJurisdictionalType: (context?: AquaticResourcesConfigContext) => string | null;
  buildValidationSchema: (baseSchema: YupObjectSchema, context?: AquaticResourcesConfigContext) => YupObjectSchema;
  formatJurisdictionalTypeString: (str?: string | null) => string | null | undefined;
  renderPermitExtras: boolean;
  renderDeleteRowsInstruction: boolean;
  sectionLabelOverride?: string;
  updateSectionEnrichment: (isDirty: boolean, isValid: boolean, isReadOnly: boolean) => UpdateSectionEnrichment;
};

export enum ArErrorMessages {
  geometryBoundaryFailure = 'To correct the aquatic resource, remove this item from the table, correct the geometry, and upload the file again.',
  invalidCowardinCode = 'The provided cowardin code is invalid',
  invalidHgmCode = 'The provided hgm code is invalid',
  invalidJdType = 'The provided jurisdictional determination type is invalid',
  invalidLatitudeFormat = 'Latitude format is incorrect. Enter a valid numeric latitude.',
  invalidLongitudeFormat = 'Longitude format is incorrect. Enter a valid numeric longitude.',
  invalidLatLongState = 'The provided coordinates (latitude/longitude) do not reside within the selected state.',
  invalidMeasurementType = 'The provided measurement type is invalid',
  invalidMeasurementUnit = 'The provided measurement unit is invalid',
  invalidResourceType = 'The provided aquatic resource type is invalid',
  invalidRow = 'Row contains an error.',
  invalidState = 'The provided state is invalid.',
  missingGeometry = 'This aquatic resource is missing geometry. Remove and re-enter this row.',
}

export const SortMap = {
  [arTableInputColumnData[0].columnLabel]: 'waterName',
  [arTableInputColumnData[1].columnLabel]: 'state',
  [arTableInputColumnData[2].columnLabel]: 'lat',
  [arTableInputColumnData[3].columnLabel]: 'long',
  [arTableInputColumnData[4].columnLabel]: 'measType',
  [arTableInputColumnData[5].columnLabel]: 'measAmount',
  [arTableInputColumnData[6].columnLabel]: 'measUnit',
  [arTableInputColumnData[7].columnLabel]: 'localWaterway',
  [arTableInputColumnData[8].columnLabel]: 'cowardinCode',
  [arTableInputColumnData[9].columnLabel]: 'hgmCode',
  [arTableInputColumnData[10].columnLabel]: 'jdType',
  [arTableInputColumnData[11].columnLabel]: 'arType',
};

export const columnToPropertyMap = {
  'Water Name/Label': 'waterName',
  State: 'state',
  Latitude: 'latitude',
  Longitude: 'longitude',
  'Measurement Amount': 'measurementAmount',
  'Measurement Type': 'measurementType',
  'Measurement Unit': 'measurementUnit',
  'JD Type': 'jurisdictionalDeterminationType',
  'Cowardin Code': 'cowardinCode',
  'Aquatic Resource Type': 'aquaticResourceType',
  'Regulation Type': 'regulationType',
  'Local Waterway': 'localWaterway',
  'HGM Code': 'hgmCode',
} as const;

export const propertyMapping = {
  jd_type: 'jurisdictionalDeterminationType',
  waters_name: 'waterName',
  state: 'state',
  latitude: 'latitude',
  longitude: 'longitude',
  meas_type: 'measurementType',
  units: 'measurementUnit',
  amount: 'measurementAmount',
  hgm_code: 'hgmCode',
  local_waterway: 'localWaterway',
  cowardin_code: 'cowardinCode',
  regulation_type: 'regulationType',
  waters_type: 'aquaticResourceType',
} as const;

export const AR_COLUMN_PROPERTY = {
  0: 'waterName',
  1: 'state',
  2: 'latitude',
  3: 'longitude',
  4: 'measurementType',
  5: 'measurementAmount',
  6: 'measurementUnit',
  7: 'localWaterway',
  8: 'cowardinCode',
  9: 'hgmCode',
  10: 'jurisdictionalDeterminationType',
  11: 'aquaticResourceType',
};

export const AR_COLUMN_INDEX = {
  waterName: 0,
  state: 1,
  latitude: 2,
  longitude: 3,
  measurementType: 4,
  measurementAmount: 5,
  measurementUnit: 6,
  localWaterway: 7,
  cowardinCode: 8,
  hgmCode: 9,
  jdType: 10,
  arType: 11,
};

export const JD_TYPE_OPTIONS = [
  { value: 'No JD Requested', label: 'No JD Requested' },
  { value: 'Preliminary', label: 'Preliminary' },
  { value: 'Approved', label: 'Approved' },
];

export const MEASUREMENT_TYPE_OPTIONS = [
  { value: 'Area', label: 'Area' },
  { value: 'Linear', label: 'Linear' },
] as const;

export const MEASUREMENT_UNIT_OPTIONS = [
  { value: 'ACRE', label: 'Acre', type: 'Area' },
  { value: 'FOOT', label: 'Foot', type: 'Linear' },
  { value: 'SQ_FT', label: 'Sq Ft.', type: 'Area' },
] as const;

const MEASUREMENT_UNIT_MAP: Record<string, string> = {
  'SQUARE FEET': 'SQ_FT',
  'SQUARE FOOT': 'SQ_FT',
  'SQUARE FT': 'SQ_FT',
  'SQUARE FT.': 'SQ_FT',
  'SQ FT': 'SQ_FT',
  'SQ. FT.': 'SQ_FT',
  'SQ FT.': 'SQ_FT',
  'SQ. FT': 'SQ_FT',
  SQ_FT: 'SQ_FT',
  ACRE: 'ACRE',
  ACRES: 'ACRE',
  ACREAGE: 'ACRE',
  FOOT: 'FOOT',
  FT: 'FOOT',
  'LINEAR FOOT': 'FOOT',
  'LINEAR FEET': 'FOOT',
  FEET: 'FOOT',
} as const;

export const cleanValue = (value: string) =>
  String(value ?? '')
    .trim()
    .replace(/°/g, '');

export const readFile = (file: File) =>
  new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve((e.target as FileReader).result as ArrayBuffer);
    reader.onerror = (e) => reject((e.target as FileReader).error);
    reader.readAsArrayBuffer(file);
  });

export const generateMultiPointGeoJson = (data: Array<{ Longitude: string; Latitude: string }>) => ({
  type: 'MultiPoint',
  coordinates: data.map((item) => [parseFloat(cleanValue(item.Longitude)), parseFloat(cleanValue(item.Latitude))]),
});

export const mapImportedRowToProperties = (row: Record<string, unknown> = {}, mapping: Record<string, string> = {}) => {
  const mappedProperties: Record<string, string> = {};

  Object.entries(row).forEach(([key, value]) => {
    const propertyKey = mapping[key] || key;
    mappedProperties[propertyKey] = cleanValue(String(value));
  });

  return mappedProperties;
};

export const getDefaultGeometry = (lat: string | number, lng: string | number) => ({
  type: 'Point',
  coordinates: [formatCoordFlt(lng) || 0, formatCoordFlt(lat) || 0],
});

export const isWaterNameUnique = (features: GeoJsonFeature[], waterName: string) => {
  if (!features?.length || !waterName) return false;

  const normalizedName = waterName.toUpperCase().trim();
  let count = 0;

  for (const feature of features) {
    if (feature?.properties?.waterName?.toUpperCase().trim() === normalizedName) {
      count++;
      if (count > 1) return false; // Early exit - count === 2, not unique
    }
  }

  return count === 1;
};

export const mapToMeasurementUnit = (val: string) => MEASUREMENT_UNIT_MAP[val?.toUpperCase()] ?? val;

export const getValidationSchema = ({
  cowardinOptions,
  hgmCodeOptions,
  location,
  regulationTypesByState,
  stateOptions,
}: {
  cowardinOptions: Option[];
  hgmCodeOptions: Option[];
  location: LatLong;
  regulationTypesByState: StateArTypes;
  stateOptions: Option[];
}) => {
  return yup.object().shape({
    jurisdictionalDeterminationType: yup
      .string()
      .nullable()
      .test({
        name: 'invalid-jd-type',
        message: ArErrorMessages.invalidJdType,
        test: (val: string) => {
          if (!val) return true;
          const valueFound = JD_TYPE_OPTIONS?.find(
            (option: Option) => option.value.toUpperCase() === val?.toUpperCase()
          );
          return valueFound;
        },
      }),

    rowIssues: yup
      .array()
      .nullable()
      .test('row-issues', function (this: { createError: (params: { message: string }) => unknown }, value: unknown) {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return true;
        }

        const message = ArErrorMessages.missingGeometry ?? ArErrorMessages.invalidRow;
        return this.createError({ message });
      }),

    waterName: yup
      .string()
      .required(ErrorMessages.Required)
      .test({
        name: 'unique-water-name',
        message: 'Water name must be unique',
        test: function (
          this: {
            options: {
              context?: AquaticResourceValidationContext;
            };
          },
          value: string | undefined
        ) {
          if (!value) {
            return true;
          }

          const duplicateWaterNameIds = this.options.context?.aquaticResourceDuplicateWaterNameIds ?? [];

          const aquaticResourceID = this.options.context?.updatedRowData?.aquaticResourceID;

          const hasDuplicateWaterName =
            aquaticResourceID != null &&
            duplicateWaterNameIds.some((duplicateId) => String(duplicateId) === String(aquaticResourceID));

          if (hasDuplicateWaterName) {
            return false;
          }

          const normalizedWaterName = value.trim().toUpperCase();
          const waterNameCounts = this.options.context?.waterNameCounts;

          if (!waterNameCounts) {
            return true;
          }

          const nameOccurranceCount = waterNameCounts.get(normalizedWaterName);

          return nameOccurranceCount === 1 || nameOccurranceCount === undefined;
        },
      }),

    state: yup
      .string()
      .required(ErrorMessages.SelectOption)
      .test({
        name: 'valid-state',
        message: ArErrorMessages.invalidLatLongState,
        test: function (this: {
          options: {
            context?: AquaticResourceValidationContext;
          };
        }) {
          const mismatchIDs = this.options.context?.aquaticResourceMismatchIds ?? [];
          const aquaticResourceID = this.options.context?.updatedRowData?.aquaticResourceID;
          const hasStateCoordinateMismatch =
            aquaticResourceID != null &&
            mismatchIDs.some((mismatchID) => String(mismatchID) === String(aquaticResourceID));
          return !hasStateCoordinateMismatch;
        },
      })
      .test({
        name: 'invalid-state',
        message: ArErrorMessages.invalidState,
        test: (val: string) => {
          const stateFound = stateOptions?.find((option: Option) => option.value.toUpperCase() === val?.toUpperCase());
          return stateFound;
        },
      }),

    latitude: yup
      .string()
      .test('latFormat', ArErrorMessages.invalidLatitudeFormat, (val?: string) => {
        if (!val?.trim()) return true; // required handles empty values
        return Number.isFinite(Number(val));
      })
      .test({
        name: 'latInRange',
        exclusive: true,
        message: ErrorMessages.InvalidLatitudeRange,
        test: (val: string) =>
          !isLatLngOutsideUs(location?.latitude, location?.longitude) ? Number(val) <= 90 && Number(val) >= 0 : true,
      })
      .test({
        name: 'geometry-boundary-failure',
        message: ErrorMessages.GeometryBoundaryFailure,
        test: function (this: {
          options: {
            context?: AquaticResourceValidationContext;
          };
        }) {
          const geometryFailureIds = this.options.context?.aquaticResourceGeometryBoundaryFailureIds ?? [];
          const aquaticResourceID = this.options.context?.updatedRowData?.aquaticResourceID;
          const hasGeometryBoundaryFailure =
            aquaticResourceID != null &&
            geometryFailureIds.some((failureID: unknown) => String(failureID) === String(aquaticResourceID));
          return !hasGeometryBoundaryFailure;
        },
      })
      .required(ErrorMessages.Required),

    longitude: yup
      .string()
      .test('lngFormat', ArErrorMessages.invalidLongitudeFormat, (val?: string) => {
        if (!val?.trim()) return true;
        return Number.isFinite(Number(val));
      })
      .test({
        name: 'lngInRange',
        exclusive: true,
        message: ErrorMessages.InvalidLongitudeRange,
        test: (val: string) =>
          !isLatLngOutsideUs(location?.latitude, location?.longitude) ? Number(val) <= 0 && Number(val) >= -180 : true,
      })
      .required(ErrorMessages.Required),

    measurementAmount: yup
      .string()
      .required(ErrorMessages.Required)
      .test('zeroTest', ErrorMessages.GreatThanZero, (val: number) => val > 0),

    measurementType: yup
      .string()
      .required(ErrorMessages.SelectOption)
      .test({
        name: 'invalid-measurement-type',
        message: ArErrorMessages.invalidMeasurementType,
        test: (val: string) => {
          const valueFound = MEASUREMENT_TYPE_OPTIONS?.find(
            (option: Option) => option.value.toUpperCase() === val?.toUpperCase()
          );
          return valueFound;
        },
      }),

    measurementUnit: yup
      .string()
      .required(ErrorMessages.SelectOption)
      .test({
        name: 'invalid-measurement-unit',
        message: ArErrorMessages.invalidMeasurementUnit,
        test: (val: string) => {
          const valueFound = MEASUREMENT_UNIT_OPTIONS?.find(
            (option: Option) => option.value.toUpperCase() === val?.toUpperCase()
          );
          return valueFound;
        },
      }),

    hgmCode: yup
      .string()
      .nullable()
      .test({
        name: 'invalid-hgm-code',
        message: ArErrorMessages.invalidHgmCode,
        test: (val: string) => {
          if (!val) return true;

          const valueFound = hgmCodeOptions?.find(
            (option: Option) => option.value.toUpperCase() === val?.toUpperCase()
          );

          return valueFound;
        },
      }),

    localWaterway: yup.string().nullable(),

    cowardinCode: yup
      .string()
      .nullable()
      .test({
        name: 'invalid-cowardin-code',
        message: ArErrorMessages.invalidCowardinCode,
        test: (val: string) => {
          if (!val) return true;

          const valueFound = cowardinOptions?.find(
            (option: Option) => option.value.toUpperCase() === val?.toUpperCase()
          );

          return valueFound;
        },
      }),

    aquaticResourceType: yup
      .string()
      .nullable()
      .test({
        name: 'invalid-aquatic-resource-state-type',
        message: 'Selected AR type is not valid for the selected state.',
        test: (val: string, context: { parent: AquaticResourceRow }) => {
          if (!val) return true;

          const stateArTypes = regulationTypesByState[context.parent.state.toUpperCase()];
          const arTypeFound = stateArTypes?.some((type) => type === val);

          return arTypeFound;
        },
      }),
  });
};

export const getOptionsByKey = <T extends Record<string, unknown>>(
  data: T[] | undefined | null,
  key: keyof T
): Option[] => {
  return (
    data?.map((item) => ({
      value: String(item[key]),
      label: String(item[key]),
    })) ?? []
  );
};

export const getRegulationTypesByState = (
  arTypes: ArType[],
  stateRegulationTypes: StateRegulationType[]
): StateArTypes => {
  const stateArTypes: StateArTypes = {};

  stateRegulationTypes.forEach((stateReg) => {
    const state = stateReg.state.toUpperCase();
    if (!stateArTypes[state]) {
      stateArTypes[state] = [];
    }

    const applicableArTypes = arTypes
      .filter((arType) => arType.regulationType === stateReg.regulationType)
      .map((arType) => arType.shortCode);

    stateArTypes[state].push(...applicableArTypes);
  });

  return stateArTypes;
};

export const TableWarnings = ({ messages }: { messages: string[] }) =>
  messages.length === 0 ? null : (
    <Alert type='warning' slim heading={`Table Warning${messages.length > 1 ? 's' : ''}`} headingLevel='h4'>
      <ul>
        {messages.map((msg: string, index: number) => (
          <li key={index}>{msg}</li>
        ))}
      </ul>
    </Alert>
  );

export const createAquaticResourcesConfig = (formType: AquaticResourcesFormType): AquaticResourcesConfig => {
  const isPermit = formType === 'permit';
  const isJd = formType === 'jd';

  return {
    formType,
    isPermit,
    isJd,

    metadata: { sectionName: 'Aquatic Resource Inventory' },

    getDefaultJurisdictionalType: (context: AquaticResourcesConfigContext = {}) => {
      if (isJd && context.jdType) {
        return context.jdType === 'Preliminary+Approved' ? null : context.jdType;
      }
      if (isPermit) {
        return null;
      }
      return null;
    },

    buildValidationSchema: (
      baseSchema: YupObjectSchema,
      context: AquaticResourcesConfigContext = {}
    ): YupObjectSchema => {
      if (!isJd || !context.determination) {
        return baseSchema;
      }

      const { determination } = context;

      return baseSchema.shape({
        jurisdictionalDeterminationType: yup
          .string()
          .nullable()
          .test(
            'invalid-jd-type',
            ArErrorMessages.invalidJdType,
            function (this: {
              options: { context?: { updatedRowData?: { jurisdictionalDeterminationType?: string } } };
            }) {
              const jdType = this.options.context?.updatedRowData?.jurisdictionalDeterminationType as
                | string
                | undefined;
              if (!jdType) return true;
              const valueFound = JD_TYPE_OPTIONS?.find((option) => option.value.toUpperCase() === jdType.toUpperCase());
              return Boolean(valueFound);
            }
          )
          .test(
            'jd-type-mismatch',
            'The aquatic resource JD type does not match the delineation JD type',
            function (jdType?: string | null) {
              if (!jdType) return true;
              const hasDeterminationType = determination.length > 0;
              const isJdRequested = hasDeterminationType && determination !== 'No JD Requested';
              const hasMismatch = isJdRequested && hasDeterminationType ? !determination.includes(jdType) : false;
              return !hasMismatch;
            }
          )
          .test(
            'no-jd-mismatch',
            'You have not selected that the USACE provide a written Jurisdictional Determination. This selection must match your aquatic resource JD type.',
            function (jdType?: string | null) {
              if (!jdType) return true;
              const hasDeterminationType = determination.length > 0;
              const noJdRequested = hasDeterminationType && determination === 'No JD Requested';
              const hasMismatch = noJdRequested && hasDeterminationType ? determination !== jdType : false;
              return !hasMismatch;
            }
          ),
      });
    },

    formatJurisdictionalTypeString: (str?: string | null) => {
      if (!isJd || !str) return str;
      const upperStr = str.toUpperCase();
      if (upperStr === 'NO JD REQUESTED') return 'No JD Requested';
      return _startCase(str);
    },

    renderPermitExtras: isPermit,
    renderDeleteRowsInstruction: isPermit,
    sectionLabelOverride: undefined,

    updateSectionEnrichment: (isDirty: boolean, isValid: boolean, isReadOnly: boolean): UpdateSectionEnrichment => {
      if (isJd) {
        return { valid: isReadOnly ? true : isValid, label: 'Aquatic Resource Inventory' };
      }
      return { valid: isReadOnly ? true : isValid, dirty: isDirty };
    },
  };
};

export const getAquaticResourceValidationIds = (
  aquaticResourceValidations: Record<string, AquaticResourceValidation> | undefined
) => {
  const mismatchIDs: number[] = [];
  const duplicateWaterNameIds: number[] = [];
  Object.entries(aquaticResourceValidations ?? {}).forEach(([aquaticResourceID, validation]) => {
    const id = Number(aquaticResourceID);
    if (!Number.isFinite(id)) {
      return;
    }
    if (validation.hasStateCoordinateMismatch) {
      mismatchIDs.push(id);
    }
    if (validation.hasDuplicateWaterName) {
      duplicateWaterNameIds.push(id);
    }
  });
  return {
    mismatchIDs,
    duplicateWaterNameIds,
  };
};
